/**
 * Custom hook for station input formatters
 * Handles formatting and validation for station fields
 */
export function useStationFormatters() {
  /**
   * Formats IATA airport code to uppercase, letters only, max 3 chars
   */
  const formatIataCode = (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
  };

  /**
   * Pads UTC offset to complete 4-digit format when user finishes typing (onBlur)
   * Examples: +8 → +0800, +05 → +0500, +530 → +0530
   */
  const padUtcOffset = (value: string): string => {
    if (!value) return '';

    const sign = value.startsWith('-') ? '-' : '+';
    const digits = value.replace(/[^\d]/g, '');

    if (!digits) return '';

    // Pad based on length
    if (digits.length === 1) return `${sign}0${digits}00`; // 8 → 0800
    if (digits.length === 2) return `${sign}${digits}00`;  // 08 → 0800
    if (digits.length === 3) return `${sign}0${digits}`;   // 530 → 0530
    if (digits.length === 4) return `${sign}${digits}`;    // 0800 → 0800

    return value;
  };

  /**
   * Formats UTC offset to backend format (no colon: +0800, -0500)
   * Backend expects ±hhmm format without colon
   * User-friendly: allows typing digits freely, auto-formats on the fly
   */
  const formatUtcOffset = (value: string): string => {
    // Empty input
    if (!value) return '';

    // Extract sign (default to +)
    const hasSign = value.startsWith('+') || value.startsWith('-');
    const sign = value.startsWith('-') ? '-' : '+';

    // Extract only digits
    const digits = value.replace(/[^\d]/g, '').slice(0, 4);

    // If no digits yet, just return the sign if user typed it
    if (!digits) return hasSign ? sign : '';

    // As user types, show what they've entered so far with sign
    // This allows them to type freely: 8, 08, 080, 0800
    return `${sign}${digits}`;
  };

  /**
   * Formats and validates time input (0000-2359 in 24-hour format)
   * Enforces valid hours (00-23) and minutes (00-59)
   */
  const formatTimeInput = (value: string): string => {
    // Only allow digits, max 4 characters
    const digits = value.replace(/[^\d]/g, '').slice(0, 4);

    if (digits.length === 0) return '';

    // Validate as user types
    if (digits.length >= 1) {
      const firstDigit = parseInt(digits[0], 10);
      // First digit of hours must be 0, 1, or 2
      if (firstDigit > 2) return '';
    }

    if (digits.length >= 2) {
      const hours = parseInt(digits.slice(0, 2), 10);
      // Hours must be 00-23
      if (hours > 23) return digits[0]; // Reset to just first digit
    }

    if (digits.length >= 3) {
      const minuteFirstDigit = parseInt(digits[2], 10);
      // First digit of minutes must be 0-5
      if (minuteFirstDigit > 5) return digits.slice(0, 2);
    }

    if (digits.length === 4) {
      const minutes = parseInt(digits.slice(2, 4), 10);
      // Minutes must be 00-59
      if (minutes > 59) return digits.slice(0, 3);
    }

    return digits;
  };

  return {
    formatIataCode,
    padUtcOffset,
    formatUtcOffset,
    formatTimeInput,
  };
}
